import React from "react";
import { StyleSheet, View } from "react-native";

import { FilterConfigItem, FilterKey } from "../../../../types";
import AnimatedFilterSection from "./Components/AnimatedFilterSection";
import FilterSectionChips from "./Components/FilterSectionChips";
import FilterSectionDropdown from "./Components/FilterSectionDropdown";
import { useAppContext } from "../../../../../Universal/API/Context-API/AppContext";

type FilterSectionProps = {
  title: FilterConfigItem["title"];
  filterKey: FilterKey;
  options: FilterConfigItem["options"];
  unavailableOptions?: FilterConfigItem["unavailableOptions"];
  presentation?: FilterConfigItem["presentation"];
  inlineLabel?: FilterConfigItem["inlineLabel"];
  placeholder?: FilterConfigItem["placeholder"];
  clearLabel?: FilterConfigItem["clearLabel"];
  selectedValues: string[];
  onToggle: (key: FilterKey, value: string) => void;
  disabled?: boolean;
};

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  filterKey,
  options,
  unavailableOptions = [],
  presentation = "chips",
  inlineLabel = false,
  placeholder,
  clearLabel,
  selectedValues,
  onToggle,
  disabled = false,
}) => {
  const { scaleAll } = useAppContext();
  const visible = options.length > 0;
  const estimatedRows = Math.ceil(options.length / 2);
  const maxHeight = Math.max(260, 80 + estimatedRows * 44);
  const styles = React.useMemo(() => createStyles(scaleAll), [scaleAll]);
  const unavailableSet = React.useMemo(
    () => new Set(unavailableOptions.map((value) => value.toLowerCase())),
    [unavailableOptions]
  );

  return (
    <AnimatedFilterSection visible={visible} maxHeight={maxHeight} durationMs={220}>
      <View style={styles.card}>
        {presentation === "dropdown" ? (
          <FilterSectionDropdown
            title={title}
            filterKey={filterKey}
            options={options}
            selectedValues={selectedValues}
            unavailableSet={unavailableSet}
            onToggle={onToggle}
            disabled={disabled}
            placeholder={placeholder}
            clearLabel={clearLabel}
          />
        ) : (
          <FilterSectionChips
            title={title}
            filterKey={filterKey}
            options={options}
            selectedValues={selectedValues}
            unavailableSet={unavailableSet}
            onToggle={onToggle}
            disabled={disabled}
            inlineLabel={inlineLabel}
          />
        )}
      </View>
    </AnimatedFilterSection>
  );
};

const createStyles = (scaleAll: number) =>
  StyleSheet.create({
    card: {
      borderRadius: 22 * scaleAll,
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 14 * scaleAll,
      backgroundColor: "#FBFBFA",
      borderWidth: 1,
      borderColor: "#ECE8E1",
    },
  });

export default React.memo(FilterSection);
